import Button from '@components/ui/ButtonV2/Button'
import CheckboxV2 from '@components/ui/CheckboxV2/CheckboxV2'
import FormControl from '@components/ui/inputs/FormControl'
import PhoneNumberInput from '@components/ui/inputs/PhoneNumberInput'
import Select from '@components/ui/inputs/Select'
import TextInput from '@components/ui/inputs/TextInput/TextInput'
import {
  customerAddressCreateFormSchema,
  customerAddressCreateFormValues,
  useCustomerAddressCreate,
} from '@features/account/api/customer-address-create'
import { useCountries } from '@features/countries/api/get-countries'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCustomErrors } from '@lib/hooks/useCustomErrors'
import useScrollList from '@lib/useScrollList'
import { selectCountry } from '@redux/globalSlice'
import { closeModal } from '@redux/modalSlice'
import { getCountryCallingCode } from 'libphonenumber-js'
import { CountryCode, getCountries } from 'libphonenumber-js/max'
import { FC, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import s from '../CustomerAddressUpdateForm/CustomerAddressUpdateForm.module.scss'

type CustomerAddressCreateFormProps = {}

export const CustomerAddressCreateForm: FC<
  CustomerAddressCreateFormProps
> = () => {
  const dispatch = useDispatch()
  const country = useSelector(selectCountry)

  const { customErrors, addError, clearErrors, loading } = useCustomErrors()
  const [loadingSpinner, setLoadingSpinner] = useState(false)

  const methods = useForm<customerAddressCreateFormValues>({
    resolver: zodResolver(customerAddressCreateFormSchema),
    values: {
      countryCode: country?.code?.toUpperCase(),
      phoneCountry: country?.code?.toUpperCase(),
      isDefaultBilling: false,
      isDefaultShipping: false,
      metadata: [{ key: 'name', value: '' }],
    },
    mode: 'onBlur',
  })
  const { mutateAsync, isPending } = useCustomerAddressCreate({
    mutationConfig: {
      onSuccess: (data) => {
        // if (data.customerAddressCreate.errors) {
        //   //go through each error and trigger an error in the form
        //   data.customerAddressCreate.errors.forEach((error) => {
        //     //trigger error
        //     if (error.type == 'AddressInvalidPostalCodeError') {
        //       methods.setError(
        //         'postalCode',
        //         {
        //           type: 'manual',
        //           message: error.message,
        //         },
        //         { shouldFocus: true },
        //       )
        //     }
        //     if (error.type == 'AddressFieldRequiredError') {
        //       if (error.field.includes('firstName')) {
        //         methods.setError(
        //           'firstName',
        //           {
        //             type: 'manual',
        //             message: error.message,
        //           },
        //           { shouldFocus: true },
        //         )
        //       } else if (error.field.includes('lastName')) {
        //         methods.setError(
        //           'lastName',
        //           {
        //             type: 'manual',
        //             message: error.message,
        //           },
        //           { shouldFocus: true },
        //         )
        //       } else if (error.field.includes('state')) {
        //         methods.setError(
        //           'state',
        //           {
        //             type: 'manual',
        //             message: error.message,
        //           },
        //           { shouldFocus: true },
        //         )
        //       }
        //     }
        //   })
        // }
      },
    },
  })

  const { data: countriesData } = useCountries({})

  methods.watch('countryCode')
  const currentCountry = countriesData?.countries?.find(
    (country) => country.code === methods.getValues('countryCode'),
  )

  const onSubmit = async (data: customerAddressCreateFormValues) => {
    setLoadingSpinner(true)

    const response = await mutateAsync({ data })

    try {
      if (response.customerAddressCreate.customerAddress) {
        clearErrors()
        setTimeout(() => dispatch(closeModal()), 1000)
      }
    } catch (error) {
      addError({
        code: 'unknown',
        message: 'Something went wrong',
      })
    } finally {
      setTimeout(() => setLoadingSpinner(false), 1000)
    }
  }

  const scrollList = document.querySelector(`.${s.root}`)
  const list = document.querySelector(`.${s.form}`)

  useScrollList(scrollList, list, s)

  return (
    <div className={s.root}>
      <FormProvider {...methods}>
        <form className={s.form} onSubmit={methods.handleSubmit(onSubmit)}>
          {customErrors && customErrors.length > 0 && (
            <div className={s.errorContainer}>
              {customErrors.map((error) => (
                <div key={error.code} className={s.error}>
                  {error.message}
                </div>
              ))}
            </div>
          )}
          <div className={s.formWrapper}>
            <div className={s.contactDetails}>
              <div className={s.headline}>Contact Details</div>
              {methods.getValues('metadata').map((metadata, index) => (
                <div key={index} className={s.metadataField}>
                  <Controller
                    control={methods.control}
                    name={`metadata.${index}.value`}
                    render={({ field, fieldState }) => (
                      <FormControl floatingLabel>
                        <FormControl.Label>
                          Address {metadata.key}
                        </FormControl.Label>
                        <TextInput className={s.inputWrapper} {...field} />
                        {fieldState.error && (
                          <FormControl.Validation variant="error">
                            {fieldState.error.message}
                          </FormControl.Validation>
                        )}
                      </FormControl>
                    )}
                  />
                </div>
              ))}
              <div className={s.firstName}>
                <Controller
                  control={methods.control}
                  name="firstName"
                  render={({
                    field: { ref, name, onChange, value },
                    fieldState,
                  }) => (
                    <FormControl required floatingLabel>
                      <FormControl.Label>First name</FormControl.Label>
                      <TextInput
                        name={name}
                        value={value}
                        onChange={onChange}
                        ref={ref}
                        className={s.inputWrapper}
                      />
                      {fieldState.error && (
                        <FormControl.Validation variant="error">
                          {fieldState.error.message}
                        </FormControl.Validation>
                      )}
                    </FormControl>
                  )}
                />
              </div>
              <div className={s.lastName}>
                <Controller
                  control={methods.control}
                  name="lastName"
                  render={({
                    field: { ref, name, onChange, value },
                    fieldState,
                  }) => (
                    <FormControl required floatingLabel>
                      <FormControl.Label>Last name</FormControl.Label>
                      <TextInput
                        name={name}
                        value={value}
                        onChange={onChange}
                        ref={ref}
                        className={s.inputWrapper}
                      />
                      {fieldState.error && (
                        <FormControl.Validation variant="error">
                          {fieldState.error?.message}
                        </FormControl.Validation>
                      )}
                    </FormControl>
                  )}
                />
              </div>

              <div className={s.email}>
                <Controller
                  control={methods.control}
                  name="email"
                  render={({
                    field: { ref, name, onChange, value },
                    fieldState,
                  }) => (
                    <FormControl floatingLabel>
                      <FormControl.Label>Email address</FormControl.Label>
                      <TextInput
                        name={name}
                        value={value}
                        onChange={onChange}
                        ref={ref}
                        className={s.inputWrapper}
                      />
                      {fieldState.error && (
                        <FormControl.Validation variant="error">
                          {fieldState.error.message}
                        </FormControl.Validation>
                      )}
                    </FormControl>
                  )}
                />
              </div>
              <div className={s.phone}>
                <Controller
                  control={methods.control}
                  name="phoneCountry"
                  render={({ field: countrySelectField }) => (
                    <Controller
                      control={methods.control}
                      name="phone"
                      render={({ field: phoneField, fieldState }) => (
                        <FormControl required floatingLabel>
                          <FormControl.Label>Phone number</FormControl.Label>
                          <PhoneNumberInput
                            {...phoneField}
                            className={s.inputWrapper}
                          >
                            <PhoneNumberInput.CountrySelect
                              required
                              {...countrySelectField}
                            >
                              {countriesData?.countries
                                ?.filter((x) =>
                                  getCountries().includes(
                                    x.code as CountryCode,
                                  ),
                                )
                                .map((country) => (
                                  <PhoneNumberInput.CountrySelect.Option
                                    key={country.code}
                                    value={country.code}
                                  >
                                    {`${country.name} (+${getCountryCallingCode(country.code as CountryCode)})`}
                                  </PhoneNumberInput.CountrySelect.Option>
                                ))}
                            </PhoneNumberInput.CountrySelect>
                          </PhoneNumberInput>
                          {fieldState.error && (
                            <FormControl.Validation variant="error">
                              {fieldState.error.message}
                            </FormControl.Validation>
                          )}
                        </FormControl>
                      )}
                    />
                  )}
                />
              </div>
            </div>

            <div className={s.addressDetails}>
              <div className={s.headline}>Address Details</div>
              <div className={s.address}>
                <Controller
                  control={methods.control}
                  name="address1"
                  render={({
                    field: { ref, name, onChange, value },
                    fieldState,
                  }) => (
                    <FormControl required floatingLabel>
                      <FormControl.Label>Address line 1</FormControl.Label>
                      <TextInput
                        name={name}
                        value={value}
                        onChange={onChange}
                        ref={ref}
                        className={s.inputWrapper}
                      />
                      {fieldState.error && (
                        <FormControl.Validation variant="error">
                          {fieldState.error.message}
                        </FormControl.Validation>
                      )}
                    </FormControl>
                  )}
                />
              </div>
              <div className={s.address2}>
                <Controller
                  control={methods.control}
                  name="address2"
                  render={({
                    field: { ref, name, onChange, value },
                    fieldState,
                  }) => (
                    <FormControl floatingLabel>
                      <FormControl.Label>Address line 2</FormControl.Label>
                      <TextInput
                        name={name}
                        value={value}
                        onChange={onChange}
                        ref={ref}
                        className={s.inputWrapper}
                      />
                      {fieldState.error && (
                        <FormControl.Validation variant="error">
                          {fieldState.error.message}
                        </FormControl.Validation>
                      )}
                    </FormControl>
                  )}
                />
              </div>
              <div className={s.company}>
                <Controller
                  control={methods.control}
                  name="company"
                  render={({
                    field: { ref, name, onChange, value },
                    fieldState,
                  }) => (
                    <FormControl floatingLabel>
                      <FormControl.Label>Company</FormControl.Label>
                      <TextInput
                        name={name}
                        value={value}
                        onChange={onChange}
                        ref={ref}
                        className={s.inputWrapper}
                      />
                      {fieldState.error && (
                        <FormControl.Validation variant="error">
                          {fieldState.error.message}
                        </FormControl.Validation>
                      )}
                    </FormControl>
                  )}
                />
              </div>
              {currentCountry?.zones.length > 0 && (
                <div className={s.state}>
                  <Controller
                    control={methods.control}
                    name="state"
                    render={({
                      field: { name, value, ref, onChange },
                      fieldState,
                    }) => (
                      <FormControl required>
                        <FormControl.Label>State</FormControl.Label>
                        <Select
                          name={name}
                          value={value}
                          onChange={onChange}
                          ref={ref}
                          className={s.inputWrapper}
                          placeholder="Select a state"
                        >
                          {currentCountry.zones.map((country) => (
                            <Select.Option
                              key={country.code}
                              value={country.code}
                            >
                              {country.name}
                            </Select.Option>
                          ))}
                        </Select>
                        {fieldState.error && (
                          <FormControl.Validation variant="error">
                            {fieldState.error.message}
                          </FormControl.Validation>
                        )}
                      </FormControl>
                    )}
                  />
                </div>
              )}
              <div className={s.postalCode}>
                <Controller
                  control={methods.control}
                  name="postalCode"
                  render={({
                    field: { ref, name, onChange, value },
                    fieldState,
                  }) => (
                    <FormControl required floatingLabel>
                      <FormControl.Label>Postal code</FormControl.Label>
                      <TextInput
                        name={name}
                        value={value}
                        onChange={onChange}
                        ref={ref}
                        className={s.inputWrapper}
                      />
                      {fieldState.error && (
                        <FormControl.Validation variant="error">
                          {fieldState.error.message}
                        </FormControl.Validation>
                      )}
                    </FormControl>
                  )}
                />
              </div>
              <div className={s.city}>
                <Controller
                  control={methods.control}
                  name="city"
                  render={({
                    field: { ref, name, onChange, value },
                    fieldState,
                  }) => (
                    <FormControl required floatingLabel>
                      <FormControl.Label>City</FormControl.Label>
                      <TextInput
                        name={name}
                        value={value}
                        onChange={onChange}
                        ref={ref}
                        className={s.inputWrapper}
                      />
                      {fieldState.error && (
                        <FormControl.Validation variant="error">
                          {fieldState.error.message}
                        </FormControl.Validation>
                      )}
                    </FormControl>
                  )}
                />
              </div>
              <div className={s.country}>
                <Controller
                  control={methods.control}
                  name="countryCode"
                  render={({
                    field: { name, value, ref, onChange },
                    fieldState,
                  }) => (
                    <FormControl required>
                      <FormControl.Label>Country</FormControl.Label>
                      <Select
                        name={name}
                        value={value}
                        onChange={onChange}
                        ref={ref}
                        className={s.inputWrapper}
                        placeholder="Select a country"
                      >
                        {!countriesData && (
                          <Select.Option value="loading">
                            {country?.name}
                          </Select.Option>
                        )}
                        {countriesData?.countries
                          ?.sort((a, b) => a.name.localeCompare(b.name))
                          ?.map((country) => (
                            <Select.Option
                              key={country.code}
                              value={country.code}
                            >
                              {country.name}
                            </Select.Option>
                          ))}
                      </Select>
                      {fieldState.error && (
                        <FormControl.Validation variant="error">
                          {fieldState.error.message}
                        </FormControl.Validation>
                      )}
                    </FormControl>
                  )}
                />
              </div>
            </div>
          </div>

          <div className={s.selectGroup}>
            <Controller
              control={methods.control}
              name="isDefaultShipping"
              render={({ field: { ref, name, onChange } }) => (
                <CheckboxV2
                  onChange={onChange}
                  name={name}
                  ref={ref}
                  id="isDefaultShipping"
                >
                  Set as default shipping address
                </CheckboxV2>
              )}
            />
            <Controller
              control={methods.control}
              name="isDefaultBilling"
              render={({ field: { ref, name, onChange } }) => (
                <CheckboxV2
                  onChange={onChange}
                  name={name}
                  ref={ref}
                  id="isDefaultBilling"
                >
                  Set as default billing address
                </CheckboxV2>
              )}
            />
          </div>

          <Button
            type="submit"
            className={s.button}
            loading={isPending || loading || loadingSpinner}
            variant="secondary"
          >
            Save address
          </Button>
        </form>
      </FormProvider>
    </div>
  )
}

export default CustomerAddressCreateForm
